export const JWT_BRIDGE_REFRESH_REQUESTED = 'JWT_BRIDGE/REFRESH_REQUESTED';

/** Max time to wait for JWT refresh before rejecting. Conservative to tolerate slow networks
 * and websocket round-trip (refresh_jwt). */
const REFRESH_TIMEOUT_MS = 10000;

let currentJwt = null;
let waiters = [];
let storeRef = null;
let refreshInFlight = false;

function parseJwtExpiry(jwt) {
  try {
    const [, payload] = jwt.split('.');
    const decoded = JSON.parse(atob(payload));
    return decoded.exp * 1000;
  } catch {
    return 0;
  }
}

function isAboutToExpire(jwt, bufferMs = 120000) {
  return parseJwtExpiry(jwt) - Date.now() < bufferMs;
}

/**
 * Called by the auth saga after refresh_jwt returns a JWT.
 * Resolves any pending getToken() callers.
 */
export function setToken(jwt) {
  currentJwt = jwt;
  refreshInFlight = false;
  const pending = waiters;
  waiters = [];
  pending.forEach(({ resolve, timeout }) => {
    clearTimeout(timeout);
    resolve(jwt);
  });
}

/**
 * Provides the Redux store reference so the bridge can dispatch
 * refresh actions when the JWT expires.
 */
export function setStore(store) {
  storeRef = store;
}

/**
 * Called by the auth saga when a refresh attempt fails.
 * Clears the in-flight flag and immediately rejects all pending
 * getToken() callers so they fail fast instead of waiting for the timeout.
 */
export function failRefresh(error) {
  refreshInFlight = false;
  const pending = waiters;
  waiters = [];
  pending.forEach(({ reject, timeout }) => {
    clearTimeout(timeout);
    reject(error);
  });
}

/**
 * Returns the current JWT. If no JWT is available yet (initial load),
 * returns a promise that resolves once the auth saga provides one via setToken().
 * If the JWT is about to expire, dispatches a refresh request to the saga
 * and waits for the fresh token (with a timeout to prevent hanging).
 */
export async function getToken() {
  if (currentJwt && !isAboutToExpire(currentJwt)) {
    return currentJwt;
  }

  // JWT is missing or about to expire — request a refresh (once per cycle)
  if (storeRef && !refreshInFlight) {
    refreshInFlight = true;
    storeRef.dispatch({ type: JWT_BRIDGE_REFRESH_REQUESTED });
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      waiters = waiters.filter(w => w.resolve !== resolve);
      reject(new Error('JWT bridge: token refresh timed out'));
    }, REFRESH_TIMEOUT_MS);

    waiters.push({ resolve, reject, timeout });
  });
}
